import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { Award, ShieldCheck, Target, Building2, Quote } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

const accreditations = [
  { name: "NAICU", src: "/logo/NAICU.png" },
  { name: "PMP", src: "/logo/PMP.png" },
  { name: "AAHEA", src: "/logo/AAHEA.png" },
  { name: "GARP", src: "/logo/GARP.jpg" },
  { name: "The Agile Alliance", src: "/logo/AgileAlliance.jpg" },
  { name: "Six Sigma Council", src: "/logo/SixSigmaCouncil.jpg" },
];

const stats = [
  { value: "2007", label: "Founded" },
  { value: "6+", label: "Global Accreditations" },
  { value: "India & US", label: "Delivery Footprint" },
  { value: "ISO 9001", label: "Certified" },
];

const highlights = [
  {
    icon: Building2,
    title: "Established in 2007",
    body: "One of India's leading IT companies, delivering cost-effective solutions across multiple technologies.",
  },
  {
    icon: Award,
    title: "Accredited & Recognized",
    body: "Accreditation from NAICU, PMP, AAHEA, GARP, The Agile Alliance and Six Sigma Council for pioneering, high-end quality work.",
  },
  {
    icon: Target,
    title: "Strategy to Delivery",
    body: "A professional services business specializing in the project landscape, transforming strategy into fit-for-purpose delivery.",
  },
  {
    icon: ShieldCheck,
    title: "Senior-Level Consultants",
    body: "Projects are staffed with senior consultants who have worked together before, ensuring tightly integrated, consistent solutions.",
  },
];

const bodyParagraphs = [
  `NetLabs IT Consultancy was established in 2007 and it has become one of India's leading IT companies. We create value for our enterprise clients by providing a whole range of cost-effective solutions and services with several technologies and helping them achieve their business objectives. We offer custom-made cost-effective solutions and substantial benefits while delivering the finest performance with integrity and commitment.`,
  `NetLabs IT Consultancy has got accreditation from NAICU, PMP, AAHEA, GARP, The Agile Alliance, Six Sigma Council for our pioneering work and high-end quality solution along with unequaled customer satisfaction.`,
  `Netlabs IT Consultancy is a professional services business specializing in the project landscape, transforming strategy into fit-for-purpose delivery. Netlabs has obtained sustained growth by providing clients with a delivery environment supported by an assurance framework that allows them to tap into a wealth of accumulated knowledge, experience, and collateral on key transformation deliverables. In our IT and Management consulting services, we use cutting-edge technologies as a tool to help organizations realize their business objectives. We staff the project with senior-level consultants that have worked together on other projects. This approach ensures the best solution for the customer and ensures that we have a tightly integrated solution. Since the consultants have worked together, no requirements will be missed or inconsistent with related functionality.`,
  `Netlabs has completed numerous projects in India and the United States, so we understand the localized business needs and governmental risk and compliance factors involved with software implementations and consulting services.`,
];

const MAP_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7007.911796388041!2d77.222092!3d28.571087!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce25ebe723353%3A0xe468b1282e8ec182!2sNetlabsIT%20Consultancy%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1785687918945!5m2!1sen!2sin";

// Justified paragraph — used throughout the page so body copy reads with a
// clean, even left-and-right edge like a formal corporate profile.
function JustifiedText({ children, sx = {} }) {
  return (
    <Typography
      sx={{
        fontSize: 15.5,
        lineHeight: 2,
        color: "text.secondary",
        textAlign: "justify",
        textJustify: "inter-word",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export default function AboutUs() {
  return (
    <Box sx={{ overflowX: "hidden" }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 13 },
          background: "linear-gradient(180deg, #0B1220 0%, #101A2E 55%, #0F172A 100%)",
        }}
      >
        {/* decorative grid + glow */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -180,
            left: "50%",
            transform: "translateX(-50%)",
            width: 620,
            height: 620,
            maxWidth: "100%",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37,99,235,0.35) 0%, transparent 68%)",
            filter: "blur(10px)",
          }}
        />

        <Box sx={{ position: "relative", maxWidth: 900, mx: "auto", px: { xs: 2, md: 4 }, textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.75,
              py: 0.6,
              borderRadius: 999,
              bgcolor: "rgba(147,197,253,0.12)",
              border: "1px solid rgba(147,197,253,0.3)",
              mb: 3,
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "#93C5FD",
                whiteSpace: "normal",
              }}
            >
              About NetLabs IT Consultancy
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 26, sm: 32, md: 48 },
              fontWeight: 800,
              color: "white",
              lineHeight: 1.15,
              mb: 2.5,
              letterSpacing: "-0.02em",
            }}
          >
            Quality Gives Confidence
          </Typography>
          <Typography sx={{ fontSize: { xs: 14, md: 17 }, color: "rgba(226,232,240,0.75)", lineHeight: 1.9, maxWidth: 680, mx: "auto" }}>
            Since 2007, we've helped enterprise clients across India and the United States turn strategy into
            fit-for-purpose delivery — with the consultants, credentials, and consistency to back it up.
          </Typography>
        </Box>

        {/* Stat strip */}
        <Box sx={{ position: "relative", maxWidth: 1000, mx: "auto", px: { xs: 2, md: 4 }, mt: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
              gap: { xs: 3, md: 0 },
              justifyItems: "center",
            }}
          >
            {stats.map((s, i) => (
              <Box
                key={s.label}
                sx={{
                  textAlign: "center",
                  px: 2,
                  width: "100%",
                  minWidth: 0,
                  borderLeft: { md: i === 0 ? "none" : "1px solid rgba(255,255,255,0.1)" },
                }}
              >
                <Typography sx={{ fontSize: { xs: 18, sm: 20, md: 26 }, fontWeight: 800, color: "white", letterSpacing: "-0.01em" }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(226,232,240,0.6)", fontWeight: 600, mt: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Body copy */}
      <Box sx={{ py: { xs: 9, md: 12 } }}>
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 340px) minmax(0, 1fr)" },
              gap: { xs: 6, md: 8 },
            }}
          >
            {/* Left rail */}
            <Box sx={{ position: { md: "sticky" }, top: 110, alignSelf: "start", minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>
                Who We Are
              </Typography>
              <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: "#0F172A", lineHeight: 1.3, mb: 3 }}>
                A trusted technology partner, built on consistency
              </Typography>
              <Box sx={{ position: "relative", pl: 3, py: 1 }}>
                <Box
                  aria-hidden
                  sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, borderRadius: "3px", bgcolor: "#2563EB" }}
                />
                <Quote size={20} color="#2563EB" style={{ marginBottom: 8 }} />
                <Typography sx={{ fontSize: 14.5, fontStyle: "italic", color: "text.secondary", lineHeight: 1.8 }}>
                  "No requirements are missed or inconsistent — because our consultants have already worked
                  together."
                </Typography>
              </Box>
            </Box>

            {/* Right column — justified body */}
            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={3.5}>
                {bodyParagraphs.map((p, i) => (
                  <JustifiedText key={i}>{p}</JustifiedText>
                ))}
              </Stack>

              {/* Accreditation logo cards */}
              <Box sx={{ mt: 5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Accreditations
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))",
                    gap: { xs: 1.25, md: 1.5 },
                  }}
                >
                  {accreditations.map((item) => (
                    <Box
                      key={item.name}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        height: { xs: 92, md: 100 },
                        px: 1.5,
                        minWidth: 0,
                        borderRadius: "14px",
                        bgcolor: "#FFFFFF",
                        border: "1px solid rgba(15,23,42,0.08)",
                        boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                        transition: "transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 14px 28px -14px rgba(37,99,235,0.35)",
                          borderColor: "rgba(37,99,235,0.28)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={item.src}
                        alt={item.name}
                        sx={{
                          height: { xs: 28, md: 32 },
                          width: { xs: 28, md: 32 },
                          objectFit: "contain",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: 10, md: 11 },
                          fontWeight: 600,
                          color: "#64748B",
                          textAlign: "center",
                          lineHeight: 1.2,
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Highlight cards */}
      <Box
  sx={{
    bgcolor: "#F8FAFC",
    pt: { xs: 0, md: 0 },
    pb: { xs: 9, md: 12 },
  }}
>
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
          <Box sx={{ textAlign: "center", maxWidth: 640, mx: "auto", mb: 6 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>
              Why NetLabs
            </Typography>
            <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: "#0F172A" }}>
              What sets our delivery apart
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 3,
              maxWidth: 920,
              mx: "auto",
            }}
          >
            {highlights.map(({ icon: Icon, title, body }) => (
              <Box
                key={title}
                sx={{
                  height: "100%",
                  minWidth: 0,
                  p: 3.5,
                  borderRadius: "18px",
                  bgcolor: "white",
                  border: "1px solid rgba(15,23,42,0.08)",
                  textAlign: "left",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 20px 40px -20px rgba(15,23,42,0.18)",
                    borderColor: "rgba(37,99,235,0.3)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "13px",
                    bgcolor: "rgba(37,99,235,0.08)",
                    color: "#2563EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2.25,
                  }}
                >
                  <Icon size={21} strokeWidth={2.1} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16.5, color: "#0F172A", mb: 1 }}>{title}</Typography>
                <Typography sx={{ fontSize: 13.75, color: "text.secondary", lineHeight: 1.85, textAlign: "justify", textJustify: "inter-word" }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Map */}
      <Box sx={{ py: { xs: 9, md: 12 } }}>
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
          <Box sx={{ textAlign: "center", maxWidth: 560, mx: "auto", mb: 4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>
              Get In Touch
            </Typography>
            <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: "#0F172A" }}>Visit Us</Typography>
          </Box>
          <Box
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 24px 48px -24px rgba(15,23,42,0.18)",
            }}
          >
            <Box
              component="iframe"
              src={MAP_EMBED_URL}
              title="NetLabs IT Consultancy location"
              width="100%"
              height="420"
              sx={{ border: 0, display: "block", maxWidth: "100%" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}